import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Footer } from '@vwfs-bronson/bronson-react';
import { formatCpDate } from '@cp-shared-15/common-utilities';
import { useAnalyticsActionTracker } from '@cp-shared-15/frontend-ui';

export const COOKIE_SETTINGS_URL = '#cookieconsent';

type LegalFooterLink = { text: string; url?: string; externalUrl?: string };

export type LegalFooterType = {
    legalTextParagraphs: { p: string }[];
    copyrightText: string;
    links: LegalFooterLink[];
};

const isExternalLink = (url: string): boolean => /^(https?:\/\/|mailto:|tel:)/.test(url);

type WindowWithCookieControl = Window & { CookieControl?: { open?: () => void } };

const openCookieSettings = (): void => {
    (window as WindowWithCookieControl).CookieControl?.open?.();
};

export const LegalFooterUi: React.FC<{ legalFooter?: LegalFooterType }> = ({ legalFooter }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { onAction: onFooterLinkClick } = useAnalyticsActionTracker('onFooterLinkClick');
    const currentYear = formatCpDate()
        .toMoment()
        .year();

    if (!legalFooter) {
        return null;
    }

    const renderLink = ({ text, url = '', externalUrl }: LegalFooterLink) => {
        const linkTarget = externalUrl || url;
        const isExternal = !!externalUrl || isExternalLink(linkTarget);

        const handleClick = (e: React.MouseEvent) => {
            onFooterLinkClick(text, linkTarget);
            if (!isExternal && linkTarget.startsWith('/')) {
                e.preventDefault();
                history.push(linkTarget);
            }
        };

        return (
            <Footer.ModuleMetaNav.Item
                key={text}
                href={linkTarget}
                normalLink={isExternal}
                linkProps={{
                    ...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
                    title: text,
                    onClick: handleClick,
                }}
            >
                {text}
            </Footer.ModuleMetaNav.Item>
        );
    };

    const cookieSettingsText = t('footer.cookie-settings');

    return (
        <Footer>
            <Footer.ModuleMetaNav
                copyrightText={legalFooter.copyrightText && `${legalFooter.copyrightText} ${currentYear}`}
                metaSectionLabel=""
            >
                {(legalFooter.links || []).map(renderLink)}
                <Footer.ModuleMetaNav.Item
                    href={COOKIE_SETTINGS_URL}
                    normalLink
                    linkProps={{
                        title: cookieSettingsText,
                        onClick: (e: React.MouseEvent) => {
                            e.preventDefault();
                            onFooterLinkClick(cookieSettingsText, COOKIE_SETTINGS_URL);
                            openCookieSettings();
                        },
                    }}
                >
                    {cookieSettingsText}
                </Footer.ModuleMetaNav.Item>
            </Footer.ModuleMetaNav>
            {legalFooter.legalTextParagraphs?.length ? (
                <Footer.ModuleFootnotes
                    footerText={legalFooter.legalTextParagraphs.map((value, index) => (
                        <p key={index}>{value.p}</p>
                    ))}
                />
            ) : null}
        </Footer>
    );
};
